import * as React from "react"
import { motion } from "motion/react"
import {
  TrendingUp,
  TrendingDown,
  Clock,
  Trophy,
  Gift,
  Zap,
  Activity,
  CalendarDays,
  Target,
  DollarSign,
  BarChart3,
  RefreshCw,
} from "lucide-react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { useAuth } from "@/contexts/AuthContext"
import { AdminCard } from "./AdminCard"
import { CHART_COLORS, CHART_PALETTE, AXIS_STYLE, GRID_STYLE, TOOLTIP_STYLE } from "./AdminChartTheme"

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const HOUR_LABELS = [
  "12a","1a","2a","3a","4a","5a","6a","7a","8a","9a","10a","11a",
  "12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p","11p",
]

const REWARD_COLORS: Record<string, string> = {
  "Cash Bonus": CHART_COLORS.emerald,
  "Points": CHART_COLORS.blue,
  "Free Spins": CHART_COLORS.violet,
  "Side Quest": CHART_COLORS.cyan,
  "Nothing": "#3A3A55",
  "Item": CHART_COLORS.amber,
}

interface DealAnalytics {
  topPerformers: Array<{
    _id: string
    name: string
    email: string
    agencyName: string
    totalDeals: number
    weeklyDeals: number
    monthlyDeals: number
  }>
  hourlyDistribution: Array<{ hour: number; count: number }>
  dayOfWeekDistribution: Array<{ day: number; count: number }>
  rewardDistribution: Array<{ type: string; count: number; totalAmount: number }>
  recentDeals: Array<{
    _id: string
    memberId: string
    submitterName: string
    slotRewardType?: string
    slotRewardAmount?: number
    createdAt: string
  }>
  welcomeRewardsClaimed: number
  welcomeRewardsTotal: number
  totalBonusPaidOut: number
  avgDealsPerDay7d: number
  avgDealsPerDay30d: number
  streakDealToday: number
  streakDealThisWeek: number
}

// Stat card with icon and trend
function StatCard({
  label,
  value,
  subValue,
  icon: Icon,
  color,
  delay,
}: {
  label: string
  value: string | number
  subValue?: string
  icon: React.ElementType
  color: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="relative overflow-hidden bg-[#111117] border border-[#1E1E2A] rounded-xl p-5"
    >
      <div
        className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-[0.07]"
        style={{ background: color }}
      />
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-white/35 font-admin">
          {label}
        </span>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${color}15` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold text-white font-mono-admin tracking-tight">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {subValue && (
        <div className="text-[11px] text-white/30 mt-1 font-admin">{subValue}</div>
      )}
    </motion.div>
  )
}

// Activity bar for the hour/day heatmap
function HeatBar({ value, max, label, color }: { value: number; max: number; label: string; color: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <div className="flex items-center gap-2 group">
      <span className="text-[10px] text-white/30 font-mono-admin w-7 text-right shrink-0">{label}</span>
      <div className="flex-1 h-5 bg-[#0C0C12] rounded-sm overflow-hidden relative">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full rounded-sm relative"
          style={{
            background: `linear-gradient(90deg, ${color}40, ${color})`,
          }}
        />
        {value > 0 && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-mono-admin text-white/50 opacity-0 group-hover:opacity-100 transition-opacity">
            {value}
          </span>
        )}
      </div>
    </div>
  )
}

// Rank badge for leaderboard
function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="text-sm">&#x1F947;</span>
  if (rank === 2) return <span className="text-sm">&#x1F948;</span>
  if (rank === 3) return <span className="text-sm">&#x1F949;</span>
  return (
    <span className="w-5 h-5 rounded-full bg-[#1E1E2A] flex items-center justify-center text-[10px] font-mono-admin text-white/40">
      {rank}
    </span>
  )
}

export function AdminDeals() {
  const { getAdminDealAnalytics } = useAuth()
  const [data, setData] = React.useState<DealAnalytics | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)

  const loadData = React.useCallback(async () => {
    const res = await getAdminDealAnalytics()
    if (res.success && res.data) {
      setData(res.data)
    }
    setLoading(false)
    setRefreshing(false)
  }, [getAdminDealAnalytics])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  const handleRefresh = () => {
    setRefreshing(true)
    loadData()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-white/40 font-admin">
        Failed to load deal analytics.
      </div>
    )
  }

  const maxHourly = Math.max(...data.hourlyDistribution.map((h) => h.count), 1)
  const maxDow = Math.max(...data.dayOfWeekDistribution.map((d) => d.count), 1)
  const totalRewards = data.rewardDistribution.reduce((sum, r) => sum + r.count, 0)

  // Velocity trend
  const velocityTrend = data.avgDealsPerDay7d - data.avgDealsPerDay30d
  const velocityUp = velocityTrend >= 0

  // Prepare day of week chart data
  const dowChartData = data.dayOfWeekDistribution.map((d) => ({
    day: DAY_LABELS[d.day],
    deals: d.count,
  }))

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white font-admin tracking-tight">Deal Analytics</h2>
          <p className="text-xs text-white/30 font-admin mt-0.5">
            Deep dive into deal patterns, performance, and rewards
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#1E1E2A] text-white/60 hover:text-white hover:bg-[#2A2A45] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Top KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Today"
          value={data.streakDealToday}
          subValue="deals submitted"
          icon={Zap}
          color="#F37020"
          delay={0}
        />
        <StatCard
          label="This Week"
          value={data.streakDealThisWeek}
          subValue="deals submitted"
          icon={CalendarDays}
          color={CHART_COLORS.blue}
          delay={0.05}
        />
        <StatCard
          label="Avg / Day (7d)"
          value={data.avgDealsPerDay7d}
          subValue={
            velocityUp
              ? `+${velocityTrend.toFixed(1)} vs 30d avg`
              : `${velocityTrend.toFixed(1)} vs 30d avg`
          }
          icon={velocityUp ? TrendingUp : TrendingDown}
          color={velocityUp ? CHART_COLORS.emerald : CHART_COLORS.red}
          delay={0.1}
        />
        <StatCard
          label="Bonus Paid Out"
          value={`$${data.totalBonusPaidOut.toLocaleString()}`}
          subValue="from slot rewards"
          icon={DollarSign}
          color={CHART_COLORS.emerald}
          delay={0.15}
        />
      </div>

      {/* Two-column layout: Leaderboard + Reward Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top Performers Leaderboard */}
        <AdminCard delay={0.2}>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-[#FFD700]" />
            <h3 className="text-sm font-semibold text-white/70 font-admin">
              Top Performers
            </h3>
          </div>
          <div className="space-y-1">
            {data.topPerformers.length === 0 ? (
              <p className="text-xs text-white/30 font-admin py-4 text-center">
                No deal submissions yet
              </p>
            ) : (
              data.topPerformers.map((agent, i) => (
                <motion.div
                  key={agent._id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.04 }}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#1A1A28] transition-colors group"
                >
                  <RankBadge rank={i + 1} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white/80 truncate font-admin">
                      {agent.name}
                    </div>
                    <div className="text-[10px] text-white/30 font-admin">
                      {agent.agencyName}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-white font-mono-admin">
                      {agent.totalDeals}
                    </div>
                    <div className="text-[10px] text-white/25 font-admin">
                      {agent.weeklyDeals}w / {agent.monthlyDeals}m
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </AdminCard>

        {/* Slot Reward Distribution */}
        <AdminCard delay={0.25}>
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-4 h-4 text-[#8B5CF6]" />
            <h3 className="text-sm font-semibold text-white/70 font-admin">
              Reward Distribution
            </h3>
            <span className="ml-auto text-[10px] text-white/25 font-mono-admin">
              {totalRewards.toLocaleString()} total
            </span>
          </div>
          {data.rewardDistribution.length === 0 ? (
            <p className="text-xs text-white/30 font-admin py-4 text-center">
              No rewards distributed yet
            </p>
          ) : (
            <div className="flex items-center gap-6">
              <div className="w-40 h-40 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.rewardDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={38}
                      outerRadius={65}
                      dataKey="count"
                      nameKey="type"
                      stroke="#111117"
                      strokeWidth={2}
                    >
                      {data.rewardDistribution.map((entry) => (
                        <Cell
                          key={entry.type}
                          fill={REWARD_COLORS[entry.type] || CHART_PALETTE[0]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      {...TOOLTIP_STYLE}
                      formatter={(value: number, name: string) => [
                        `${value} (${totalRewards > 0 ? Math.round((value / totalRewards) * 100) : 0}%)`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 flex-1">
                {data.rewardDistribution.map((r) => (
                  <div key={r.type} className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{
                        background: REWARD_COLORS[r.type] || CHART_PALETTE[0],
                      }}
                    />
                    <span className="text-[11px] text-white/50 font-admin flex-1 truncate">
                      {r.type}
                    </span>
                    <span className="text-[11px] font-semibold text-white/70 font-mono-admin">
                      {r.count}
                    </span>
                    {r.totalAmount > 0 && r.type === "Cash Bonus" && (
                      <span className="text-[10px] text-emerald-400/60 font-mono-admin">
                        ${r.totalAmount}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </AdminCard>
      </div>

      {/* Deals by Day of Week */}
      <AdminCard delay={0.3}>
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-[#3B82F6]" />
          <h3 className="text-sm font-semibold text-white/70 font-admin">
            Deals by Day of Week
          </h3>
        </div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dowChartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradDow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.blue} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={CHART_COLORS.blue} stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis
                dataKey="day"
                {...AXIS_STYLE}
                tick={{ fill: "#52525B", fontSize: 11 }}
              />
              <YAxis
                {...AXIS_STYLE}
                tick={{ fill: "#52525B", fontSize: 10 }}
              />
              <Tooltip
                {...TOOLTIP_STYLE}
                formatter={(value: number) => [`${value} deals`, "Volume"]}
              />
              <Bar
                dataKey="deals"
                fill="url(#gradDow)"
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </AdminCard>

      {/* Hourly Activity Heatmap */}
      <AdminCard delay={0.35}>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-[#F37020]" />
          <h3 className="text-sm font-semibold text-white/70 font-admin">
            Hourly Activity (UTC)
          </h3>
          <span className="ml-auto text-[10px] text-white/25 font-mono-admin">
            peak: {HOUR_LABELS[data.hourlyDistribution.reduce((a, b) => b.count > a.count ? b : a, data.hourlyDistribution[0]).hour]}
          </span>
        </div>
        <div className="space-y-[3px]">
          {data.hourlyDistribution.map((h) => (
            <HeatBar
              key={h.hour}
              value={h.count}
              max={maxHourly}
              label={HOUR_LABELS[h.hour]}
              color="#F37020"
            />
          ))}
        </div>
      </AdminCard>

      {/* Bottom row: Welcome Rewards + Recent Deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Welcome Reward Stats */}
        <AdminCard delay={0.4}>
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[#10B981]" />
            <h3 className="text-sm font-semibold text-white/70 font-admin">
              Welcome Rewards
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0C0C12] rounded-lg p-4 border border-[#1E1E2A]/50">
              <div className="text-[10px] uppercase tracking-wider text-white/30 font-admin">
                Claimed
              </div>
              <div className="text-2xl font-bold text-emerald-400 font-mono-admin mt-1">
                {data.welcomeRewardsClaimed}
              </div>
              <div className="text-[10px] text-white/25 font-admin mt-0.5">
                via first deal
              </div>
            </div>
            <div className="bg-[#0C0C12] rounded-lg p-4 border border-[#1E1E2A]/50">
              <div className="text-[10px] uppercase tracking-wider text-white/30 font-admin">
                Claim Rate
              </div>
              <div className="text-2xl font-bold text-white font-mono-admin mt-1">
                {data.welcomeRewardsTotal > 0
                  ? Math.round(
                      (data.welcomeRewardsClaimed / data.welcomeRewardsTotal) * 100
                    )
                  : 0}
                %
              </div>
              <div className="text-[10px] text-white/25 font-admin mt-0.5">
                of {data.welcomeRewardsTotal} eligible
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4">
            <div className="h-2 bg-[#0C0C12] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: `${
                    data.welcomeRewardsTotal > 0
                      ? (data.welcomeRewardsClaimed / data.welcomeRewardsTotal) * 100
                      : 0
                  }%`,
                }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="h-full rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${CHART_COLORS.emerald}80, ${CHART_COLORS.emerald})`,
                }}
              />
            </div>
          </div>
        </AdminCard>

        {/* Recent Deals Feed */}
        <AdminCard delay={0.45}>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-[#06B6D4]" />
            <h3 className="text-sm font-semibold text-white/70 font-admin">
              Recent Deals
            </h3>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar">
            {data.recentDeals.length === 0 ? (
              <p className="text-xs text-white/30 font-admin py-4 text-center">
                No deals yet
              </p>
            ) : (
              data.recentDeals.map((deal, i) => (
                <motion.div
                  key={deal._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 + i * 0.03 }}
                  className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-[#1A1A28] transition-colors"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-white/70 font-admin truncate">
                        {deal.submitterName}
                      </span>
                      <span className="text-[10px] text-white/20 font-mono-admin">
                        #{deal.memberId}
                      </span>
                    </div>
                  </div>
                  {deal.slotRewardType && deal.slotRewardType !== "nothing" && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded font-admin shrink-0"
                      style={{
                        background: `${
                          REWARD_COLORS[
                            deal.slotRewardType === "bonus"
                              ? "Cash Bonus"
                              : deal.slotRewardType === "points"
                              ? "Points"
                              : deal.slotRewardType === "free_spins"
                              ? "Free Spins"
                              : deal.slotRewardType === "side_quest"
                              ? "Side Quest"
                              : "Item"
                          ] || CHART_PALETTE[0]
                        }20`,
                        color:
                          REWARD_COLORS[
                            deal.slotRewardType === "bonus"
                              ? "Cash Bonus"
                              : deal.slotRewardType === "points"
                              ? "Points"
                              : deal.slotRewardType === "free_spins"
                              ? "Free Spins"
                              : deal.slotRewardType === "side_quest"
                              ? "Side Quest"
                              : "Item"
                          ] || CHART_PALETTE[0],
                      }}
                    >
                      {deal.slotRewardType === "bonus" && deal.slotRewardAmount
                        ? `+$${deal.slotRewardAmount}`
                        : deal.slotRewardType === "points" && deal.slotRewardAmount
                        ? `+${deal.slotRewardAmount}pts`
                        : deal.slotRewardType}
                    </span>
                  )}
                  <span className="text-[10px] text-white/20 font-mono-admin shrink-0">
                    {formatTimeAgo(deal.createdAt)}
                  </span>
                </motion.div>
              ))
            )}
          </div>
        </AdminCard>
      </div>
    </div>
  )
}

function formatTimeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "now"
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d`
  return `${Math.floor(days / 30)}mo`
}
